import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import {
  CheckIcon,
  PlayIcon,
  TrashIcon,
  PlusIcon,
  ImageIcon,
  XIcon,
  FileTextIcon,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { useAppDispatch } from '@/redux/hooks'
import { updateRoomDetails } from '@/redux/asyncThunks/room'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { toast } from 'sonner'
import { GameRoomDescriptionField } from './GameRoomDescription'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { EventMediaGeneratorChat } from './EventMediaGeneratorChat'
import { EventMediaItem, GameroomEvent } from '@/redux/types/room'
import {
  addSecondsToDateTime,
  convertUTCToLocal,
  getCurrentLocalDateTime,
} from '@/utils/dateTimeUtils'

const DEFAULT_VALUES = {
  eventTitle: 'Emergency Event',
  eventText:
    'A critical situation has developed that requires immediate attention and coordinated response from all team members.',
}

interface QuickAddEventProps {
  events: GameroomEvent[]
  isGameCompleted?: boolean
  isGameInProgress?: boolean
  onEventsUpdate?: () => void
}

export default function QuickAddEvent({
  events,
  isGameCompleted = false,
  isGameInProgress = false,
  onEventsUpdate,
}: QuickAddEventProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [releasingEventId, setReleasingEventId] = useState<number | null>(null)
  const [unreleasingEventId, setUnreleasingEventId] = useState<number | null>(
    null
  )
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mediaGeneratorEventId, setMediaGeneratorEventId] = useState<
    number | null
  >(null)
  const [previewMediaUrl, setPreviewMediaUrl] = useState<string | null>(null)
  const [mediaToRemove, setMediaToRemove] = useState<{
    eventId: number
    mediaId: number
  } | null>(null)

  const isGameStarted = isGameInProgress || isGameCompleted
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string

  const { currentRoom } = useSelector((state: RootState) => state.room)

  const formatDateTime = (datetime: string): string => {
    if (!datetime) return ''
    try {
      const date = new Date(datetime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    } catch (error) {
      return datetime
    }
  }

  const getDayLabel = (index: number): string => {
    return index === 0 ? 'Day 0 - ' : ''
  }

  const updateEvents = async (updatedEvents: GameroomEvent[]) => {
    // Deliberately no audience fields: the server leaves an absent audience
    // alone, so this panel cannot wipe what Scenario & Events set.
    const eventsWithSeconds = updatedEvents.map((event) => ({
      id: event.id,
      title: event.title,
      text: event.text,
      event_datetime: new Date(addSecondsToDateTime(event.event_datetime)),
      is_released: event.is_released,
      media: event.media ?? [],
    }))

    await dispatch(
      updateRoomDetails({
        roomId,
        scenario_title: currentRoom?.scenario_title?.toString(),
        events: eventsWithSeconds,
        ends_in: currentRoom?.ends_in,
        start_time: currentRoom?.start_time
          ? new Date(currentRoom.start_time)
          : undefined,
        status: currentRoom?.status!,
      })
    ).unwrap()
  }

  const handleReleaseEvent = async (eventId: number) => {
    setReleasingEventId(eventId)
    try {
      const eventIndex = events.findIndex(
        (event) => (event.id || events.indexOf(event)) === eventId
      )
      if (eventIndex !== -1) {
        const updatedEvents = [...events]
        updatedEvents[eventIndex] = {
          ...updatedEvents[eventIndex],
          is_released: true,
        }
        await updateEvents(updatedEvents)
        toast.success('Event released successfully!')
        onEventsUpdate?.()
      }
    } catch (error) {
      toast.error('Failed to release event')
    } finally {
      setReleasingEventId(null)
    }
  }

  const handleUnreleaseEvent = async (eventId: number) => {
    setUnreleasingEventId(eventId)
    try {
      const eventIndex = events.findIndex(
        (event) => (event.id || events.indexOf(event)) === eventId
      )
      if (eventIndex !== -1) {
        const updatedEvents = [...events]
        updatedEvents[eventIndex] = {
          ...updatedEvents[eventIndex],
          is_released: false,
        }
        await updateEvents(updatedEvents)
        toast.success('Event unreleased successfully!')
        onEventsUpdate?.()
      }
    } catch (error) {
      toast.error('Failed to unrelease event')
    } finally {
      setUnreleasingEventId(null)
    }
  }

  const handleDeleteEvent = async (eventIndex: number) => {
    if (eventIndex === 0) return

    try {
      const updatedEvents = events.filter((_, i) => i !== eventIndex)
      await updateEvents(updatedEvents)
      toast.success('Event deleted successfully!')
      onEventsUpdate?.()
      setShowDeleteDialog(false)
      setEventToDelete(null)
    } catch (error) {
      toast.error('Failed to delete event')
    }
  }

  const handleAddEvent = async (values: {
    title: string
    text: string
    event_datetime: string
  }) => {
    setIsSubmitting(true)
    try {
      const newEvent: GameroomEvent = {
        id: Date.now(),
        title: values.title,
        text: values.text,
        event_datetime: values.event_datetime,
        is_released: !isGameStarted,
      }

      const updatedEvents = [...events, newEvent]
      await updateEvents(updatedEvents)
      toast.success('Event added successfully!')
      setShowAddForm(false)
      onEventsUpdate?.()
    } catch (error) {
      toast.error('Failed to add event')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className='w-full'>
      <CardContent className='space-y-4 my-5'>
        {/* Events List */}
        <div className='space-y-2 max-h-64 overflow-y-auto'>
          {events.map((event, index) => (
            <div
              key={event.id || index}
              className={`space-y-2 p-3 rounded-lg border ${
                index === 0
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className='flex items-center justify-between'>
                <div className='flex-1 min-w-0'>
                  <div className='text-sm font-medium text-gray-900 truncate'>
                    {getDayLabel(index)}
                    {event.title}
                  </div>
                  <div className='text-xs text-gray-500'>
                    {formatDateTime(event.event_datetime)}
                  </div>
                </div>

                <div className='flex items-center gap-2 ml-4'>
                  {!isGameCompleted && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => setMediaGeneratorEventId(event.id)}
                            className='text-dark-blue hover:bg-blue-50'
                          >
                            <ImageIcon className='w-4 h-4' />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Generate media for this event</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {event.is_released ? (
                    <div className='flex items-center gap-1'>
                      <CheckIcon className='w-4 h-4 text-green-500' />
                      {!isGameCompleted && (
                        <button
                          onClick={() =>
                            handleUnreleaseEvent(event.id || index)
                          }
                          className='p-1 hover:bg-gray-200 rounded text-orange-600'
                          disabled={
                            unreleasingEventId === (event.id || index)
                          }
                          type='button'
                          title='Unrelease event'
                        >
                          {unreleasingEventId === (event.id || index) ? (
                            <div className='w-4 h-4 animate-spin rounded-full border-2 border-orange-600 border-t-transparent' />
                          ) : (
                            <PlusIcon className='w-4 h-4 rotate-45' />
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className='flex items-center gap-1'>
                      {!isGameCompleted && (
                        <button
                          onClick={() =>
                            handleReleaseEvent(event.id || index)
                          }
                          className='p-1 hover:bg-gray-200 rounded text-green-600'
                          disabled={
                            releasingEventId === (event.id || index)
                          }
                          type='button'
                          title='Release event'
                        >
                          {releasingEventId === (event.id || index) ? (
                            <div className='w-4 h-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent' />
                          ) : (
                            <PlayIcon className='w-4 h-4 fill-green-600' />
                          )}
                        </button>
                      )}

                      {!isGameCompleted && index > 0 && (
                        <button
                          onClick={() => {
                            setEventToDelete(index)
                            setShowDeleteDialog(true)
                          }}
                          className='p-1 hover:bg-gray-200 rounded text-red-500'
                          type='button'
                          title='Delete event'
                        >
                          <TrashIcon className='w-4 h-4' />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {event.media && event.media.length > 0 && (
                <div className='pt-2 mt-2 border-t border-gray-200 min-w-0'>
                  <span className='text-sm font-medium text-muted-foreground'>
                    Media:
                  </span>
                  <div className='flex gap-2 overflow-x-auto pb-1 mt-1 pt-3 px-1'>
                    {event.media.map((m) => (
                      <div key={m.id} className='relative inline-block shrink-0'>
                        {m.type === 'image' && (
                          <button
                            type='button'
                            onClick={() => setPreviewMediaUrl(m.url)}
                            className='rounded border overflow-hidden hover:opacity-90 hover:ring-2 hover:ring-dark-blue/30 focus:outline-none focus:ring-2 focus:ring-dark-blue focus:ring-offset-1 transition-shadow'
                          >
                            <img
                              src={m.url}
                              alt='Event media'
                              className='h-14 w-14 object-cover block'
                            />
                          </button>
                        )}
                        {m.type === 'document' && (
                          <a
                            href={m.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-xs text-blue-800 border border-blue-200 transition-colors'
                            title='Open TTQ document'
                          >
                            <FileTextIcon className='w-3 h-3 flex-shrink-0' />
                            TTQ Doc
                          </a>
                        )}
                        {!isGameCompleted && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type='button'
                                  variant='secondary'
                                  size='icon'
                                  className='absolute -top-2 -right-2 h-6 w-6 rounded-full border border-red-200 bg-white shadow-sm text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setMediaToRemove({
                                      eventId: event.id ?? index,
                                      mediaId: m.id,
                                    })
                                  }}
                                >
                                  <XIcon className='h-4 w-4' />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side='top'>
                                <p>Remove media from event</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Event Section */}
        {!isGameCompleted && (
          <div className='border-t pt-4'>
            {!showAddForm ? (
              <Button
                type='button'
                onClick={() => setShowAddForm(true)}
                className='w-full bg-blue-600 hover:bg-blue-700 text-dark-blue'
              >
                <PlusIcon className='w-4 h-4 mr-2' />
                Add New Event
              </Button>
            ) : (
              <Formik
                initialValues={{
                  title: DEFAULT_VALUES.eventTitle,
                  text: DEFAULT_VALUES.eventText,
                  event_datetime:
                    events.length > 0
                      ? convertUTCToLocal(
                          events[events.length - 1].event_datetime
                        )
                      : getCurrentLocalDateTime(),
                }}
                onSubmit={handleAddEvent}
              >
                {({ isValid }) => (
                  <Form className='space-y-4'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Event Title
                      </label>
                      <Field
                        as={Input}
                        name='title'
                        placeholder='Enter event title'
                        className='w-full'
                      />
                      <ErrorMessage
                        name='title'
                        component='div'
                        className='text-red-500 text-sm mt-1'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Event Date & Time
                      </label>
                      <Field
                        as={Input}
                        type='datetime-local'
                        name='event_datetime'
                        className='w-full'
                      />
                      <ErrorMessage
                        name='event_datetime'
                        component='div'
                        className='text-red-500 text-sm mt-1'
                      />
                    </div>

                    <div>
                      <GameRoomDescriptionField name='text' readOnly={false} />
                    </div>

                    <div className='flex gap-2'>
                      <Button
                        type='submit'
                        disabled={!isValid || isSubmitting}
                        className='flex-1 bg-green-600 hover:bg-green-700 text-white'
                      >
                        {isSubmitting ? 'Adding...' : 'Add Event'}
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => setShowAddForm(false)}
                        className='flex-1'
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        )}

        <ConfirmationModal
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false)
            setEventToDelete(null)
          }}
          onConfirm={() =>
            eventToDelete !== null && handleDeleteEvent(eventToDelete)
          }
          title='Confirm Deletion'
          description='Are you sure you want to delete this event? This action cannot be undone.'
          confirmButtonText='Delete'
          cancelButtonText='Cancel'
        />

        <Dialog
          open={previewMediaUrl !== null}
          onOpenChange={(open) => !open && setPreviewMediaUrl(null)}
        >
          <DialogContent className='max-w-4xl'>
            <DialogHeader>
              <DialogTitle>Media preview</DialogTitle>
            </DialogHeader>
            {previewMediaUrl && (
              <img
                src={previewMediaUrl}
                alt='Media preview'
                className='max-w-full max-h-[70vh] w-auto h-auto object-contain rounded border'
              />
            )}
          </DialogContent>
        </Dialog>

        <ConfirmationModal
          isOpen={mediaToRemove !== null}
          onClose={() => setMediaToRemove(null)}
          onConfirm={async () => {
            if (mediaToRemove === null) return
            const eventIndex =
              events.findIndex((e) => e.id === mediaToRemove.eventId) >= 0
                ? events.findIndex((e) => e.id === mediaToRemove.eventId)
                : mediaToRemove.eventId
            const updatedEvents = events.map((e, i) =>
              i === eventIndex
                ? {
                    ...e,
                    media: (e.media ?? []).filter(
                      (m) => m.id !== mediaToRemove.mediaId
                    ),
                  }
                : e
            )
            try {
              await updateEvents(updatedEvents)
              onEventsUpdate?.()
              setMediaToRemove(null)
            } catch {
              // toast already in updateEvents or thunk
            }
          }}
          title='Remove media'
          description='Are you sure you want to remove this media from the event? You can add it again later by generating new media.'
          confirmButtonText='Remove'
          cancelButtonText='Cancel'
        />

        {mediaGeneratorEventId !== null && roomId && (
          <EventMediaGeneratorChat
            open={mediaGeneratorEventId !== null}
            onOpenChange={(open) => !open && setMediaGeneratorEventId(null)}
            roomId={roomId}
            eventId={mediaGeneratorEventId}
            scenarioTitle={currentRoom?.scenario_title ?? ''}
            eventTitle={
              events.find((e) => e.id === mediaGeneratorEventId)?.title ?? ''
            }
            eventText={
              events.find((e) => e.id === mediaGeneratorEventId)?.text ?? ''
            }
            existingMedia={
              events.find((e) => e.id === mediaGeneratorEventId)?.media
            }
            onAccept={(media: EventMediaItem) => {
              const updatedEvents = events.map((e) =>
                e.id === mediaGeneratorEventId
                  ? { ...e, media: [...(e.media ?? []), media] }
                  : e
              )
              updateEvents(updatedEvents).then(() => {
                setMediaGeneratorEventId(null)
                onEventsUpdate?.()
              })
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}
